import {
	closeBrackets,
	closeBracketsKeymap,
	completionKeymap,
} from "@codemirror/autocomplete";
import {
	defaultKeymap,
	history,
	historyKeymap,
	indentWithTab,
	insertNewlineAndIndent,
} from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import {
	bracketMatching,
	foldEffect,
	foldGutter,
	HighlightStyle,
	indentOnInput,
	syntaxHighlighting,
} from "@codemirror/language";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { Compartment, EditorSelection, EditorState } from "@codemirror/state";
import {
	crosshairCursor,
	drawSelection,
	dropCursor,
	EditorView,
	highlightActiveLine,
	highlightActiveLineGutter,
	keymap,
	lineNumbers,
	rectangularSelection,
} from "@codemirror/view";
import { tags } from "@lezer/highlight";
import { getActiveI18n } from "@magic-md/i18n";
import { useCallback, useEffect, useRef } from "react";
import { magicMDIntelligence } from "../lib/codemirror-intelligence";
import { magicBookmarks } from "../lib/codemirror-bookmarks";
import { githubAlertHighlight } from "../lib/codemirror-alert";
import { getFileExtension, isMarkdownFile } from "../lib/file-type";
import {
	handleSmartIntakeFromDataTransfer,
	handleSmartIntakeFromFilePaths,
	hasSmartIntakeData,
} from "../lib/smart-intake";
import {
	buildFoldEffects,
	fullDocumentReplaceAnnotation,
	getFoldSnapshot,
	isMagicFoldCloseLineText,
	magicMDSyntax,
} from "../lib/codemirror-magic-syntax";
import {
	FOR_CLOSE_RE,
	FOR_HEADER_RE,
} from "../lib/codemirror-magic-syntax/patterns";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";

/* ── Syntax highlighting theme (module-level constant) ── */

const markdownHighlighting = syntaxHighlighting(
	HighlightStyle.define([
		// Headings
		{
			tag: tags.heading,
			textDecoration: "none",
			fontWeight: "500",
			color: "var(--syntax-heading)",
		},
		{ tag: tags.heading1, fontSize: "1.15em", color: "var(--syntax-heading)" },
		{ tag: tags.heading2, fontSize: "1.08em", color: "var(--syntax-heading)" },
		{ tag: tags.heading3, fontSize: "1.02em", color: "var(--syntax-heading)" },
		// Emphasis
		{
			tag: tags.emphasis,
			fontStyle: "italic",
			color: "var(--syntax-emphasis)",
		},
		{ tag: tags.strong, fontWeight: "600", color: "var(--syntax-strong)" },
		{
			tag: tags.strikethrough,
			textDecoration: "line-through",
			color: "var(--syntax-strikethrough)",
		},
		// Links
		{
			tag: tags.link,
			color: "inherit",
			textDecoration: "none",
		},
		{ tag: tags.url, color: "var(--syntax-link)" },
		// Code
		{
			tag: [tags.monospace, tags.processingInstruction],
			color: "var(--syntax-code)",
		},
		// Quotes & Lists
		{ tag: tags.quote, fontStyle: "italic", color: "var(--syntax-quote)" },
		{ tag: tags.list, color: "var(--syntax-list)" },
		{ tag: tags.contentSeparator, color: "var(--syntax-separator)" },
		// Keywords
		{
			tag: [tags.keyword, tags.controlKeyword, tags.operatorKeyword, tags.self],
			color: "var(--syntax-keyword)",
		},
		// Strings
		{
			tag: [tags.string, tags.character, tags.attributeValue, tags.regexp],
			color: "var(--syntax-string)",
		},
		// Numbers & constants
		{
			tag: [
				tags.number,
				tags.integer,
				tags.float,
				tags.bool,
				tags.atom,
				tags.null,
				tags.escape,
			],
			color: "var(--syntax-number)",
		},
		// Functions
		{
			tag: [
				tags.function(tags.variableName),
				tags.function(tags.definition(tags.variableName)),
			],
			color: "var(--syntax-function)",
		},
		// Variables & Properties
		{
			tag: [
				tags.variableName,
				tags.propertyName,
				tags.definition(tags.variableName),
				tags.labelName,
			],
			color: "var(--syntax-variable)",
		},
		// Types
		{
			tag: [tags.typeName, tags.className, tags.namespace],
			color: "var(--syntax-type)",
		},
		// HTML Tags & Attributes
		{ tag: tags.tagName, color: "var(--syntax-tag)" },
		{ tag: tags.attributeName, color: "var(--syntax-attribute)" },
		// Comments
		{
			tag: [tags.comment, tags.blockComment, tags.lineComment, tags.docComment],
			fontStyle: "italic",
			color: "var(--syntax-comment)",
		},
		{ tag: tags.meta, color: "var(--syntax-comment)" },
		// Operators & Punctuation
		{ tag: tags.operator, color: "var(--syntax-operator)" },
		{
			tag: [
				tags.punctuation,
				tags.bracket,
				tags.paren,
				tags.squareBracket,
				tags.brace,
				tags.angleBracket,
			],
			color: "var(--syntax-bracket)",
		},
	]),
	{ fallback: true },
);

const DEFAULT_INDENT_UNIT = "    ";
const SMART_INTAKE_PATH_DROP_EVENT = "magic-md:smart-intake-path-drop";

interface SmartIntakePathDropEventDetail {
	paths: string[];
	position?: { x: number; y: number };
	source?: "drop" | "import";
	handled?: boolean;
}

function getLeadingWhitespace(text: string): string {
	return /^\s*/.exec(text)?.[0] ?? "";
}

function detectIndentUnit(lineIndent: string): string {
	return lineIndent.includes("\t") ? "\t" : DEFAULT_INDENT_UNIT;
}

function findEnclosingForHeaderLine(
	state: EditorState,
	lineNumber: number,
): number | null {
	let nestedForDepth = 0;

	for (let currentLineNumber = lineNumber; currentLineNumber >= 1; currentLineNumber -= 1) {
		const trimmed = state.doc.line(currentLineNumber).text.trim();

		if (FOR_CLOSE_RE.test(trimmed)) {
			nestedForDepth += 1;
			continue;
		}

		if (!FOR_HEADER_RE.test(trimmed)) continue;

		if (nestedForDepth === 0) {
			return currentLineNumber;
		}

		nestedForDepth -= 1;
	}

	return null;
}

export function insertMagicForNewline(view: EditorView): boolean {
	const { state } = view;
	const { main } = state.selection;
	if (!main.empty) return false;

	const line = state.doc.lineAt(main.head);
	const currentIndent = getLeadingWhitespace(line.text);
	const currentTrimmed = line.text.trim();
	const isForHeaderLine = FOR_HEADER_RE.test(currentTrimmed);
	const enclosingForHeaderLine = findEnclosingForHeaderLine(state, line.number);

	if (!isForHeaderLine && enclosingForHeaderLine === null) {
		return false;
	}

	if (FOR_CLOSE_RE.test(currentTrimmed)) {
		return false;
	}

	const nextIndent = isForHeaderLine
		? `${currentIndent}${detectIndentUnit(currentIndent)}`
		: currentIndent;

	view.dispatch(
		state.update({
			changes: { from: main.head, to: main.head, insert: `\n${nextIndent}` },
			selection: EditorSelection.cursor(main.head + 1 + nextIndent.length),
			userEvent: "input",
		}),
	);
	return true;
}

interface UseCodeMirrorOptions {
	initialContent: string;
	onChange: (content: string) => void;
	paneId?: string;
	readOnly?: boolean;
}

/**
 * A hook that manages a CodeMirror 6 editor instance.
 * Returns a ref callback to mount the editor onto a container element.
 */
export function useCodeMirror({
	initialContent,
	onChange,
	paneId,
	readOnly = false,
}: UseCodeMirrorOptions) {
	const syntaxHoverDetailEnabled = useEditorStore(
		(s) => s.syntaxHoverDetailEnabled,
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const viewRef = useRef<EditorView | null>(null);
	const onChangeRef = useRef(onChange);
	const initialContentRef = useRef(initialContent);
	const paneIdRef = useRef(paneId);
	const readOnlyRef = useRef(readOnly);
	const pendingCursorRef = useRef<{ line: number; col: number } | null>(null);
	const cursorFrameRef = useRef<number | null>(null);
	const isComposingRef = useRef(false);
	// Suppress onChange during programmatic replaceContent to avoid marking dirty
	const isReplacingRef = useRef(false);
	// rAF handle for debounced doc.toString() — coalesces rapid keystrokes
	const contentRafRef = useRef<number | null>(null);
	// Compartments for swapping language / magic-syntax when tab filePath changes
	const languageCompartmentRef = useRef(new Compartment());
	const magicCompartmentRef = useRef(new Compartment());
	const readOnlyCompartmentRef = useRef(new Compartment());
	const currentFilePathRef = useRef<string | null>(null);
	const loadedExtensionRef = useRef<string | null>(null);

	const buildReadOnlyExtensions = useCallback(
		() => [
			EditorView.editable.of(!readOnlyRef.current),
			EditorState.readOnly.of(readOnlyRef.current),
		],
		[],
	);

	// Keep the callback reference fresh without re-creating the editor
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		readOnlyRef.current = readOnly;
		viewRef.current?.dispatch({
			effects: readOnlyCompartmentRef.current.reconfigure(
				buildReadOnlyExtensions(),
			),
		});
	}, [buildReadOnlyExtensions, readOnly]);

	// Store initialContent in a ref so mountRef callback is stable
	useEffect(() => {
		initialContentRef.current = initialContent;
	}, [initialContent]);

	const applySyntaxHoverDetailClass = useCallback((enabled: boolean) => {
		viewRef.current?.dom.classList.toggle(
			"cm-magic-hide-hover-detail",
			!enabled,
		);
	}, []);

	useEffect(() => {
		applySyntaxHoverDetailClass(syntaxHoverDetailEnabled);
	}, [applySyntaxHoverDetailClass, syntaxHoverDetailEnabled]);

	const resolveCurrentTabId = useCallback((): string | null => {
		const pane = paneIdRef.current;
		if (pane) {
			const paneEntry = useEditorStore
				.getState()
				.panes.find((entry) => entry.paneId === pane);
			return paneEntry?.tabId ?? useEditorStore.getState().activeTabId ?? null;
		}
		return useEditorStore.getState().activeTabId ?? null;
	}, []);

	const resolveCurrentFilePath = useCallback((): string | null => {
		const tabId = resolveCurrentTabId();
		if (!tabId) return null;
		const tab = useEditorStore.getState().tabs.find((t) => t.id === tabId);
		return tab?.filePath ?? null;
	}, [resolveCurrentTabId]);

	const updateEditorDomScope = useCallback(() => {
		const view = viewRef.current;
		if (!view) return;
		const pane = paneIdRef.current;
		const tabId = resolveCurrentTabId();
		if (pane) {
			view.dom.dataset.magicPaneId = pane;
		} else {
			delete view.dom.dataset.magicPaneId;
		}
		if (tabId) {
			view.dom.dataset.magicTabId = tabId;
		} else {
			delete view.dom.dataset.magicTabId;
		}
	}, [resolveCurrentFilePath, resolveCurrentTabId]);

	useEffect(() => {
		paneIdRef.current = paneId;
		updateEditorDomScope();
	}, [paneId, updateEditorDomScope]);

	useEffect(() => {
		let previousPaneId: string | null | undefined;
		let previousTabId: string | null | undefined;
		const syncEditorDomScope = () => {
			const nextPaneId = paneIdRef.current ?? null;
			const nextTabId = resolveCurrentTabId();
			if (nextPaneId === previousPaneId && nextTabId === previousTabId) {
				return;
			}
			previousPaneId = nextPaneId;
			previousTabId = nextTabId;
			updateEditorDomScope();
		};
		syncEditorDomScope();
		return useEditorStore.subscribe(syncEditorDomScope);
	}, [resolveCurrentTabId, updateEditorDomScope]);

	useEffect(() => {
		const clientPointForView = (
			position: { x: number; y: number } | undefined,
			view: EditorView,
		): { x: number; y: number } | null => {
			if (!position) return null;
			const rect = view.dom.getBoundingClientRect();
			const ratio = window.devicePixelRatio || 1;
			const candidates = [
				{ x: position.x, y: position.y },
				{ x: position.x / ratio, y: position.y / ratio },
			];
			return (
				candidates.find(
					(point) =>
						point.x >= rect.left &&
						point.x <= rect.right &&
						point.y >= rect.top &&
						point.y <= rect.bottom,
				) ?? null
			);
		};

		const shouldHandlePathDrop = (
			detail: SmartIntakePathDropEventDetail,
			view: EditorView,
		): { x: number; y: number } | "active" | null => {
			const point = clientPointForView(detail.position, view);
			if (point) return point;
			if (detail.position) return null;
			const activeTabId = useEditorStore.getState().activeTabId;
			return view.dom.dataset.magicTabId === activeTabId ||
				view.dom.contains(document.activeElement)
				? "active"
				: null;
		};

		const handler = (event: Event) => {
			const detail = (event as CustomEvent<SmartIntakePathDropEventDetail>).detail;
			if (
				!detail ||
				detail.handled ||
				!Array.isArray(detail.paths) ||
				detail.paths.length === 0
			) {
				return;
			}
			const view = viewRef.current;
			if (!view || readOnlyRef.current) return;
			const target = shouldHandlePathDrop(detail, view);
			if (!target) return;
			detail.handled = true;
			if (target !== "active") {
				const pos = view.posAtCoords(target);
				if (pos !== null) {
					view.dispatch({ selection: EditorSelection.cursor(pos) });
				}
			}
			void handleSmartIntakeFromFilePaths(
				detail.source ?? "drop",
				detail.paths,
				view,
				resolveCurrentFilePath(),
			);
		};

		window.addEventListener(SMART_INTAKE_PATH_DROP_EVENT, handler);
		return () => window.removeEventListener(SMART_INTAKE_PATH_DROP_EVENT, handler);
	}, [resolveCurrentFilePath]);

	// Build magic-syntax + intelligence extensions (only for markdown files)
	const buildMagicExtensions = useCallback(() => {
		return [
			...magicMDSyntax(),
			...githubAlertHighlight(),
			...magicMDIntelligence({
				getEngine: () => getPlatformService().engine,
				getSessionId: () => {
					const tabId = resolveCurrentTabId();
					if (!tabId) return null;
					return (
						usePluginExecutionStore
							.getState()
							.tabSessionMap.get(tabId) ?? null
					);
				},
				getSyncVersion: () => {
					const tabId = resolveCurrentTabId();
					if (!tabId) return 0;
					return (
						usePluginExecutionStore
							.getState()
							.tabSyncVersionMap.get(tabId) ?? 0
					);
				},
				getLocale: () => getActiveI18n().locale,
				getCurrentFilePath: resolveCurrentFilePath,
				ensureSession: () =>
					usePluginExecutionStore.getState()._ensureSession(),
				subscribeSessionId: (listener) => {
					let prevSessionId: string | null = null;
					const check = () => {
						const tabId = resolveCurrentTabId();
						const sessionId = tabId
							? (usePluginExecutionStore
									.getState()
									.tabSessionMap.get(tabId) ?? null)
							: null;
						if (sessionId !== prevSessionId) {
							const prev = prevSessionId;
							prevSessionId = sessionId;
							listener(sessionId, prev);
						}
					};
					const unsub1 = usePluginExecutionStore.subscribe(check);
					const unsub2 = useEditorStore.subscribe(check);
					return () => {
						unsub1();
						unsub2();
					};
				},
				subscribeSyncVersion: (listener) => {
					let prevSyncVersion = -1;
					const check = () => {
						const tabId = resolveCurrentTabId();
						const syncVersion = tabId
							? (usePluginExecutionStore
									.getState()
									.tabSyncVersionMap.get(tabId) ?? 0)
							: 0;
						if (syncVersion !== prevSyncVersion) {
							const prev = prevSyncVersion;
							prevSyncVersion = syncVersion;
							listener(syncVersion, prev);
						}
					};
					const unsub1 = usePluginExecutionStore.subscribe(check);
					const unsub2 = useEditorStore.subscribe(check);
					return () => {
						unsub1();
						unsub2();
					};
				},
			}),
		];
	}, [resolveCurrentTabId]);

	// Async-load a CodeMirror language by file extension via @codemirror/language-data
	const loadLanguageForExtension = useCallback(async (ext: string) => {
		if (!ext) return null;
		const desc = languages.find((l) =>
			l.extensions.some((e) => e.toLowerCase() === ext.toLowerCase()),
		);
		if (!desc) return null;
		try {
			return await desc.load();
		} catch {
			return null;
		}
	}, []);

	const flushCursorPosition = useCallback(() => {
		cursorFrameRef.current = null;
		if (isComposingRef.current) return;
		const pending = pendingCursorRef.current;
		if (!pending) return;

		const store = useEditorStore.getState();
		if (paneIdRef.current) {
			store.setPaneCursorPosition(paneIdRef.current, pending.line, pending.col);
			return;
		}

		store.setCursorPosition(pending.line, pending.col);
	}, []);

	// Stable mount callback
	const mountRef = useCallback(
		(node: HTMLDivElement | null) => {
			// Clean up previous editor
			if (viewRef.current) {
				viewRef.current.destroy();
				viewRef.current = null;
			}
			if (cursorFrameRef.current !== null) {
				cancelAnimationFrame(cursorFrameRef.current);
				cursorFrameRef.current = null;
			}
			// Flush any pending rAF content sync before destroying the view
			if (contentRafRef.current !== null) {
				cancelAnimationFrame(contentRafRef.current);
				contentRafRef.current = null;
			}
			pendingCursorRef.current = null;

			containerRef.current = node;
			if (!node) return;

			// Seed currentFilePathRef so the initial language compartment matches
			// the active tab's filePath even before the reconcile effect runs.
			currentFilePathRef.current = resolveCurrentFilePath();

			const updateListener = EditorView.updateListener.of((update) => {
				if (update.docChanged && !isReplacingRef.current) {
					// Coalesce rapid keystrokes: schedule doc.toString() on the
					// next animation frame instead of on every transaction.
					// This reduces 3.8 MB allocations from 30+/sec to ~16/sec
					// (one per frame).  Downstream consumers (parseMagicMd at
					// 150 ms, session sync at 300 ms) already debounce, so the
					// ≤1-frame delay is invisible.
					if (contentRafRef.current !== null) {
						cancelAnimationFrame(contentRafRef.current);
					}
					contentRafRef.current = requestAnimationFrame(() => {
						contentRafRef.current = null;
						if (viewRef.current && !isReplacingRef.current) {
							onChangeRef.current(viewRef.current.state.doc.toString());
						}
					});
				}
				// Track cursor position changes
				if (update.selectionSet || update.docChanged) {
					const pos = update.state.selection.main.head;
					const line = update.state.doc.lineAt(pos);
					const col = pos - line.from + 1;
					pendingCursorRef.current = { line: line.number, col };
					if (!isComposingRef.current && cursorFrameRef.current === null) {
						cursorFrameRef.current = requestAnimationFrame(flushCursorPosition);
					}
				}
			});

			const domEventHandlers = EditorView.domEventHandlers({
				compositionstart() {
					isComposingRef.current = true;
					return false;
				},
				compositionend() {
					isComposingRef.current = false;
					if (pendingCursorRef.current && cursorFrameRef.current === null) {
						cursorFrameRef.current = requestAnimationFrame(flushCursorPosition);
					}
					return false;
				},
				paste(event, view) {
					const data = event.clipboardData;
					if (readOnlyRef.current || !data || !hasSmartIntakeData(data)) {
						return false;
					}
					event.preventDefault();
					void handleSmartIntakeFromDataTransfer(
						"paste",
						data,
						view,
						resolveCurrentFilePath(),
					);
					return true;
				},
				drop(event, view) {
					const data = event.dataTransfer;
					if (readOnlyRef.current || !data || !hasSmartIntakeData(data)) {
						return false;
					}
					const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
					if (pos !== null) {
						view.dispatch({ selection: EditorSelection.cursor(pos) });
					}
					event.preventDefault();
					void handleSmartIntakeFromDataTransfer(
						"drop",
						data,
						view,
						resolveCurrentFilePath(),
					);
					return true;
				},
			});

			const theme = EditorView.theme({
				"&": {
					height: "100%",
				},
				".cm-scroller": {
					overflowX: "hidden",
					overflowY: "auto",
				},
			});

			const state = EditorState.create({
				doc: initialContentRef.current,
				extensions: [
					// Bookmarks gutter MUST be added before lineNumbers() so it
					// sits to the left of the line-number column (where users
					// instinctively right-click to manage bookmarks).
					magicBookmarks({
						getFilePath: () => {
							const tabId = resolveCurrentTabId();
							if (!tabId) return null;
							const tab = useEditorStore
								.getState()
								.tabs.find((t) => t.id === tabId);
							return tab?.filePath ?? null;
						},
					}),
					// Basic setup
					lineNumbers(),
					highlightActiveLineGutter(),
					history(),
					foldGutter({
						domEventHandlers: {
							click(view, line) {
								return isMagicFoldCloseLineText(
									view.state.doc.lineAt(line.from).text,
								);
							},
						},
					}),
					dropCursor(),
					EditorState.allowMultipleSelections.of(true),
					indentOnInput(),
					markdownHighlighting,
					bracketMatching(),
					closeBrackets(),
					rectangularSelection(),
					drawSelection(),
					crosshairCursor(),
					highlightActiveLine(),
					highlightSelectionMatches(),

					// Keymaps
					keymap.of([
						{
							key: "Enter",
							run: insertMagicForNewline,
							shift: insertNewlineAndIndent,
						},
						...closeBracketsKeymap,
						...completionKeymap,
						...defaultKeymap,
						...searchKeymap,
						...historyKeymap,
						indentWithTab,
					]),

					// Markdown / language (compartmentized: swapped on tab change)
					languageCompartmentRef.current.of(
						isMarkdownFile(currentFilePathRef.current ?? undefined)
							? markdown({ base: markdownLanguage })
							: [],
					),

					// Magic MD plugin syntax + intelligence (only for markdown)
					magicCompartmentRef.current.of(
						isMarkdownFile(currentFilePathRef.current ?? undefined)
							? buildMagicExtensions()
							: [],
					),

					// Listener & theme
					domEventHandlers,
					updateListener,
					theme,
					readOnlyCompartmentRef.current.of(buildReadOnlyExtensions()),

					// Soft wrap
					EditorView.lineWrapping,
				],
			});

			viewRef.current = new EditorView({
				state,
				parent: node,
			});
			updateEditorDomScope();
			applySyntaxHoverDetailClass(
				useEditorStore.getState().syntaxHoverDetailEnabled,
			);

			const initialFoldEffects = buildFoldEffects(
				{ state: viewRef.current.state },
				{ unfolded: new Set() },
			);
			if (initialFoldEffects.length > 0) {
				viewRef.current.dispatch({ effects: initialFoldEffects });
			}
		},
		[
			applySyntaxHoverDetailClass,
			flushCursorPosition,
			resolveCurrentTabId,
			resolveCurrentFilePath,
			buildMagicExtensions,
			buildReadOnlyExtensions,
			updateEditorDomScope,
		],
	);

	// Reconfigure language / magic-syntax compartments when the active tab's
	// filePath changes (or when an untitled tab gets a path on save).
	useEffect(() => {
		const reconcile = async (filePath: string | null) => {
			if (filePath === currentFilePathRef.current && filePath !== null) {
				// Same file — nothing to do (avoid redundant async loads).
				return;
			}
			// For untitled tabs (filePath === null), only reconcile if changing.
			if (
				filePath === currentFilePathRef.current &&
				loadedExtensionRef.current !== null &&
				loadedExtensionRef.current !== ""
			) {
				return;
			}
			currentFilePathRef.current = filePath;

			const view = viewRef.current;
			if (!view) return;

			if (isMarkdownFile(filePath ?? undefined)) {
				loadedExtensionRef.current = "md";
				view.dispatch({
					effects: [
						languageCompartmentRef.current.reconfigure(
							markdown({ base: markdownLanguage }),
						),
						magicCompartmentRef.current.reconfigure(buildMagicExtensions()),
					],
				});
				return;
			}

			const ext = getFileExtension(filePath ?? undefined);
			loadedExtensionRef.current = ext;
			const lang = await loadLanguageForExtension(ext);

			// Bail if filePath changed again during the async load
			if (currentFilePathRef.current !== filePath) return;
			const liveView = viewRef.current;
			if (!liveView) return;

			liveView.dispatch({
				effects: [
					languageCompartmentRef.current.reconfigure(lang ?? []),
					magicCompartmentRef.current.reconfigure([]),
				],
			});
		};

		let previousFilePath: string | null | undefined;
		const syncLanguageForFilePath = () => {
			const filePath = resolveCurrentFilePath();
			if (filePath === previousFilePath) return;
			previousFilePath = filePath;
			void reconcile(filePath);
		};

		syncLanguageForFilePath();
		const unsub = useEditorStore.subscribe(syncLanguageForFilePath);
		return unsub;
	}, [resolveCurrentFilePath, buildMagicExtensions, loadLanguageForExtension]);

	/**
	 * Programmatically replace the entire document (e.g. when switching tabs
	 * or when plugin output is serialized back).
	 *
	 * Strategy: instead of fragile line-number-based fold save/restore,
	 * we track which define-block IDs the user has **manually unfolded**
	 * and keep those unfolded. Everything else gets folded by default.
	 * This ensures new/updated blocks are always folded.
	 */
	const replaceContent = useCallback(
		(content: string, options?: { preserveSelection?: boolean }) => {
			const view = viewRef.current;
			if (!view) return;
			const currentContent = view.state.doc.toString();
			if (currentContent === content) return;
			const preserveSelection = options?.preserveSelection ?? true;

			// Before replacing, snapshot which blocks are currently UNFOLDED
			// (user explicitly opened them). Tracks define blocks, config, and output.
			const snapshot = getFoldSnapshot(view);

			// Save scroll position before replacing document
			const scrollTop = view.scrollDOM.scrollTop;

			// Save cursor (selection) position so we can restore it after the
			// full-document replacement.  A full replace maps the cursor to the
			// end of the inserted text which causes CodeMirror's deferred measure
			// cycle to scroll the viewport down to the cursor.
			const oldSelection = view.state.selection;
			const newDocLen = content.length;
			const anchor = preserveSelection
				? Math.min(oldSelection.main.anchor, newDocLen)
				: 0;
			const head = preserveSelection
				? Math.min(oldSelection.main.head, newDocLen)
				: 0;

			// A tab switch intentionally resets the cursor to offset 0. If the old
			// editor still has focus, the magic syntax layer treats the first block in
			// the new document as actively edited and renders it in L2/raw mode. Blur
			// before the replace so the incoming document paints in its L0 pretty form;
			// EditorPanel's tab-switch autofocus can restore focus without forcing L2.
			if (!preserveSelection && view.hasFocus) {
				view.contentDOM.blur();
			}

			// Replace content and restore folds in a single sequential transaction
			// so large documents don't paint an intermediate expanded frame.
			isReplacingRef.current = true;
			const replaceTransaction = view.state.update({
				annotations: fullDocumentReplaceAnnotation.of(true),
				changes: { from: 0, to: view.state.doc.length, insert: content },
			});
			const foldEffects = buildFoldEffects(
				{ state: replaceTransaction.state },
				snapshot,
			);

			// CM6's foldState clears any fold whose range contains the cursor
			// (clearTouchedFolds in @codemirror/language). After a full replace
			// the old cursor offset may land inside a newly-created fold — e.g.
			// when execution adds an output fence and the block grows. Move the
			// cursor to the fold boundary so folds are preserved.
			const foldRanges: { from: number; to: number }[] = [];
			for (const eff of foldEffects) {
				if (eff.is(foldEffect)) {
					foldRanges.push(eff.value);
				}
			}
			const clampOutsideFolds = (pos: number): number => {
				for (const r of foldRanges) {
					if (pos > r.from && pos < r.to) return r.from;
				}
				return pos;
			};
			const safeAnchor = clampOutsideFolds(anchor);
			const safeHead = clampOutsideFolds(head);

			const foldTransaction = replaceTransaction.state.update({
				effects: foldEffects,
				selection:
					safeAnchor === safeHead
						? EditorSelection.cursor(safeAnchor)
						: EditorSelection.single(safeAnchor, safeHead),
			});
			view.dispatch([replaceTransaction, foldTransaction]);
			isReplacingRef.current = false;

			// Restore scroll position after both document change and fold application
			view.scrollDOM.scrollTop = scrollTop;

			// Re-enforce scroll position after CodeMirror's deferred measure cycle
			// which may fire in the next animation frame and adjust scroll.
			requestAnimationFrame(() => {
				view.scrollDOM.scrollTop = scrollTop;
			});
		},
		[],
	);

	/** Focus the editor */
	const focus = useCallback(() => {
		viewRef.current?.focus();
	}, []);

	return { mountRef, replaceContent, focus, viewRef };
}
